import { FC, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { galleryService, GalleryItem } from '../services/galleryService';

// Fallback images for when API is not available
import projekt1 from '../assets/projekt-1.png';
import bydgoszcz from '../assets/Bydgoszcz2.jpeg';
import czarnkowo from '../assets/Czarnkowo2.jpeg';
import lubasz from '../assets/Lubasz2.jpeg';
import pila from '../assets/Piła2.jpeg';
import trzcianek from '../assets/Trzcianek2.jpeg';

interface ProjectCardProps {
  title: string;
  category: string;
  imageUrl: string;
  index: number;
}

const ProjectCard: FC<ProjectCardProps> = ({ title, category, imageUrl, index }) => {
  return (
    <motion.div 
      className="group relative overflow-hidden rounded-lg cursor-pointer"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <motion.div 
        className="h-64 bg-gray-200"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <img 
          src={imageUrl} 
          alt={title} 
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div 
        className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
      >
        <div className="absolute bottom-0 left-0 p-4 text-white">
          <p className="text-sm font-medium">{category}</p>
          <h3 className="text-xl font-bold">{title}</h3>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projects: FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Fallback projects for when API is not available
  const fallbackProjects = [
    {
      title: 'Nowoczesny ogród zimowy',
      category: 'Ogrody zimowe',
      imageUrl: projekt1,
    },
    {
      title: 'Zabudowa tarasu w rezydencji',
      category: 'Zabudowa tarasów',
      imageUrl: bydgoszcz,
    },
    {
      title: 'System bezramowy w apartamencie',
      category: 'System bezramowy',
      imageUrl: czarnkowo,
    },
    {
      title: 'Zabudowa balkonu w bloku',
      category: 'Zabudowa balkonów',
      imageUrl: lubasz,
    },
    {
      title: 'Zadaszenie tarasu w domu jednorodzinnym',
      category: 'Zadaszenia tarasów',
      imageUrl: pila,
    },
    {
      title: 'Daszek nad wejściem do budynku',
      category: 'Daszki',
      imageUrl: trzcianek,
    },
  ];

  useEffect(() => {
    const fetchGalleryItems = async () => {
      try {
        console.log('🔄 Projects: Ładuję elementy galerii...');
        const items = await galleryService.getGalleryItems();
        console.log('✅ Projects: Otrzymano', items.length, 'elementów galerii');
        setGalleryItems(items.slice(0, 6)); // Pokazuj maksymalnie 6 elementów
      } catch (error) {
        console.error('❌ Projects: Error fetching gallery items:', error);
        // W przypadku błędu zostaw pustą tablicę - użyjemy fallback
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryItems();
  }, []);

  // Użyj danych z galerii jeśli są dostępne, w przeciwnym razie fallback
  const projects = galleryItems.length > 0 
    ? galleryItems.map(item => ({
        title: item.title,
        category: item.category || 'Realizacja',
        imageUrl: item.images.length > 0 ? item.images[0].url : fallbackProjects[0].imageUrl,
      }))
    : fallbackProjects;

  if (loading) {
    return (
      <section id="projects" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="projects" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8">
        <motion.h2 
          className="text-3xl font-bold text-center mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7 }}
          ref={ref}
        >
          Nasze Realizacje
        </motion.h2>
        <motion.p 
          className="text-center text-gray-600 mb-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          Zobacz wybrane projekty, które zrealizowaliśmy dla naszych klientów.
          Każda realizacja to indywidualne podejście i dostosowanie do potrzeb inwestora.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <ProjectCard key={index} {...project} index={index} />
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/gallery">
            <motion.button 
              className="bg-blue-700 text-white font-bold py-3 px-8 rounded-md hover:bg-blue-800 transition duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              Zobacz więcej realizacji
            </motion.button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Projects;
